import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useMarkerValuations } from '../services/markerValuationService';
import { activateSafeHarbor } from '../services/safeHarborService';

/*
 * Managed Governance — Safe Harbor Activation.
 *
 * The single surface where a screened, collateral-backed participant activates
 * Safe Harbor. Activation establishes the beta-tested blockchain comms channel
 * (Canton Network MainNet) that routes the participant into the private market.
 *
 * This is intended to be the ONLY unlocked page for Safe Harbor activation.
 */

const SAFE_HARBOR_URL = 'https://www.kromarketplace.com/people-market';
const LEDGER_PATH = '/manage-your-ledger';

const usd = (n) =>
  typeof n === 'number'
    ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    : '—';

function useUserIdentity() {
  const { instance } = useMsal();
  return useMemo(() => {
    let account = instance.getActiveAccount?.();
    if (!account) {
      const all = instance.getAllAccounts?.() || [];
      if (all.length) account = all[0];
    }
    const email =
      account?.username ||
      account?.idTokenClaims?.preferred_username ||
      account?.idTokenClaims?.email ||
      '';
    const name = account?.name || account?.idTokenClaims?.name || email || 'Account Holder';
    return { name, email };
  }, [instance]);
}

export default function ManagedGovernance() {
  const navigate = useNavigate();
  const { name, email } = useUserIdentity();
  const { markers, data, isLive } = useMarkerValuations();
  const [activated, setActivated] = useState(null); // { channel, rail, at } once activated
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState(null);

  const markerRows = Object.values(markers || {});
  const summary = data?.portfolio_summary || {};

  // Readiness gates that must be satisfied before Safe Harbor can be activated.
  const gates = [
    { id: 'screening', label: 'Personnel screening cleared', detail: 'KYC / AML / Sanctions on Trust Ledger', ready: true },
    { id: 'collateral', label: 'Collateral evidence confirmed', detail: `${markerRows.length || summary.total_markers || 0} record(s) · ${usd(summary.total_value_usd)}`, ready: markerRows.length > 0 },
    { id: 'documents', label: 'Governance documents on file', detail: 'Term sheet, charter, UCC perfection', ready: true },
    { id: 'comms', label: 'Blockchain comms channel', detail: `Canton Network MainNet · beta ${isLive ? '(live feed)' : '(cached)'}`, ready: true },
  ];

  const allReady = gates.every((g) => g.ready);

  const activate = async () => {
    if (activating) return;
    setActivating(true);
    setActivateError(null);
    try {
      const result = await activateSafeHarbor({
        participantName: name || 'Account Holder',
        participantEmail: email,
        collateral: {
          records: markerRows.length || summary.total_markers || 0,
          valueUsd: summary.total_value_usd ?? null,
        },
      });
      setActivated({
        channel: result.channel,
        rail: result.rail || 'Canton Network MainNet',
        at: new Date(result.activatedAt || Date.now()).toLocaleString(),
        reused: !!result.reused,
      });
    } catch (e) {
      setActivateError(e.message || 'Activation failed. Please try again.');
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-white to-slate-50 border border-slate-200 p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-purple-700 mb-1">
              <i className="bi bi-diagram-3-fill" /> Safe Harbor · Managed Governance
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Managed Governance</h1>
            <p className="text-slate-600 mt-1 text-sm">
              {name ? `${name}, ` : ''}activate Safe Harbor to route into the private market over our
              beta-tested blockchain comms channel.
            </p>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
              activated
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-amber-50 text-amber-700 border border-amber-200'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${activated ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            {activated ? 'Safe Harbor active' : 'Not activated'}
          </span>
        </div>
      </div>

      {/* Readiness checklist */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5">
        <h2 className="font-semibold mb-1">Activation Readiness</h2>
        <p className="text-xs text-slate-500 mb-4">All gates must be green before Safe Harbor can be activated.</p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {gates.map((g) => (
            <li key={g.id} className="flex items-start justify-between rounded-xl border border-slate-200 px-4 py-3">
              <div>
                <div className="text-sm font-medium text-slate-700">{g.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{g.detail}</div>
              </div>
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                  g.ready ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'
                }`}
              >
                <i className={`bi ${g.ready ? 'bi-check-circle-fill' : 'bi-hourglass-split'}`} />
                {g.ready ? 'Ready' : 'Pending'}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Activation panel */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5">
        {activated ? (
          <div className="text-center py-4">
            <i className="bi bi-shield-check text-emerald-600 d-block mb-2" style={{ fontSize: '2.4rem' }} />
            <h3 className="text-lg font-bold">Safe Harbor activated</h3>
            <p className="text-sm text-slate-600 mt-1">
              Your comms channel is live on Canton Network MainNet. You’re routed into the private market.
            </p>
            <div className="mx-auto mt-3 max-w-md text-left rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm">
              <div className="flex justify-between py-1 border-b border-slate-200">
                <span className="text-slate-500">Comms channel</span>
                <span className="font-mono text-xs font-semibold">{activated.channel}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-200">
                <span className="text-slate-500">Rail</span>
                <span className="font-semibold">{activated.rail}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-slate-500">Activated</span>
                <span className="font-semibold">{activated.at}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 justify-center mt-4">
              <a
                href={SAFE_HARBOR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2.5 rounded-lg bg-purple-700 text-white hover:bg-purple-800 font-semibold text-sm"
              >
                <i className="bi bi-box-arrow-up-right me-1" /> Enter Safe Harbor
              </a>
              <button
                onClick={() => navigate(LEDGER_PATH)}
                className="px-4 py-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 border border-slate-200 font-semibold text-sm"
              >
                Back to My Ledger
              </button>
            </div>
          </div>
        ) : (
          <div className="max-w-2xl">
            <h2 className="font-semibold mb-1">Activate Safe Harbor</h2>
            <p className="text-sm text-slate-600 mb-4">
              Activation establishes your blockchain comms channel and routes you into the Safe Harbor
              private market. Investors and vetted participants are onboarded through this single surface.
            </p>
            <ol className="text-sm text-slate-600 space-y-2 mb-5 list-decimal list-inside">
              <li>Confirm all readiness gates above are green.</li>
              <li>Activate to open a Canton Network comms channel bound to your Trust Ledger.</li>
              <li>Enter the Safe Harbor private market over the established channel.</li>
            </ol>
            <button
              onClick={activate}
              disabled={!allReady || activating}
              className={`px-5 py-2.5 rounded-lg font-semibold text-sm text-white ${
                allReady && !activating ? 'bg-purple-700 hover:bg-purple-800' : 'bg-slate-300 cursor-not-allowed'
              }`}
            >
              <i className={`bi ${activating ? 'bi-arrow-repeat' : 'bi-shield-lock-fill'} me-1`} />
              {activating ? 'Activating…' : 'Activate Safe Harbor'}
            </button>
            {!allReady && (
              <p className="text-xs text-amber-600 mt-2">
                Complete the pending readiness gates before activation.
              </p>
            )}
            {activateError && (
              <p className="text-xs text-red-600 mt-2">
                <i className="bi bi-exclamation-triangle me-1" />
                {activateError}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Scope note */}
      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-xs text-slate-500">
        <i className="bi bi-info-circle me-1" />
        Managed Governance is the only surface where Safe Harbor activation is performed. Comms routing uses
        the beta-tested Canton Network channel; participants are onboarded here after screening and collateral
        confirmation on the Trust Ledger.
      </div>
    </div>
  );
}
